import { EField as EEpcField } from "@rentiohq/shared-frontend/dist/forms/epc/schema.epcInfo.types";
import { mapFormFieldToNullableBooleanValue } from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import { IUpsertPropertyAmenityItem } from "@rentiohq/shared-frontend/dist/reduxV2/propertyAmenity/propertyAmenity.types";
import * as rentRegistrationActions from "@rentiohq/shared-frontend/dist/reduxV2/rentRegistration/rentRegistration.actions";
import * as rentRegistrationSelectors from "@rentiohq/shared-frontend/dist/reduxV2/rentRegistration/rentRegistration.selectors";
import * as rentRegistrationDocumentsActions from "@rentiohq/shared-frontend/dist/reduxV2/rentRegistrationDocuments/rentRegistrationDocuments.actions";
import * as rentRegistrationDocumentsSelectors from "@rentiohq/shared-frontend/dist/reduxV2/rentRegistrationDocuments/rentRegistrationDocuments.selectors";
import { IContract } from "@rentiohq/shared-frontend/dist/types/contract.types";
import { IProperty } from "@rentiohq/shared-frontend/dist/types/property.types";
import {
  ERegistrationDocumentStatus,
  ERegistrationDocumentType,
  ERegistrationMemberType,
  ICreateRegistrationDocument,
} from "@rentiohq/shared-frontend/dist/types/registration.types";
import { EAN_PREFIX } from "forms/editPropertyExtraInfo/schema.editPropertyExtraInfo.config";
import { IRootStore } from "redux/reducers";
import { EField, IExtraData } from "./schema.registrationAddDocument.types";

// Amenity name constants
const AMENITY_NAMES = {
  buildingFloor: "rentio.default.building_floor",
  buildingFloorCount: "rentio.default.building_floor_count",
  roomCountBedroom: "rentio.default.room_count_bedroom",
  roomCountBathroom: "rentio.default.room_count_bathroom",
  roomCountGarage: "rentio.default.room_count_garage",
  buildingHasStorage: "rentio.default.building_has_storage",
  isFurnished: "rentio.default.is_furnished",
  hasOven: "rentio.default.has_oven",
  hasFridge: "rentio.default.has_fridge",
  hasDishwasher: "rentio.default.has_dishwasher",
  hasRangeHood: "rentio.default.has_range_hood",
  hasLift: "rentio.default.has_lift",
  hasDoubleGlazing: "rentio.default.has_double_glazing",
  hasCentralHeating: "rentio.default.has_central_heating",
  heatingType: "rentio.default.heating_type",
  buildingSurfaceArea: "rentio.default.building_surface_area",
  buildingAdjoined: "rentio.default.building_adjoined",
} as const;

const createAmenityItem = (
  propertyAmenities: IExtraData["propertyAmenities"],
  name: string,
  value: string | number | boolean | undefined | null,
  dataType: "string" | "number" | "boolean",
): IUpsertPropertyAmenityItem | null => {
  // Check if there's an existing amenity for this name
  const existingAmenity = propertyAmenities?.find(a => a.name === name);

  // If value is undefined and no existing amenity, don't include
  if (value === undefined && !existingAmenity) return null;

  // If value is undefined but there's an existing amenity, send null to delete it
  if (value === undefined && existingAmenity) {
    return {
      id: existingAmenity.id,
      name,
      dataType,
      value: null,
    };
  }

  // If value is null (explicitly cleared) and there's an existing amenity, send null to delete
  if (value === null && existingAmenity) {
    return {
      id: existingAmenity.id,
      name,
      dataType,
      value: null,
    };
  }

  // If value is null/undefined and no existing amenity, don't include
  if (value === null || value === undefined) return null;

  return {
    id: existingAmenity?.id,
    name,
    dataType,
    value,
  };
};

export const isPerformingSubmitActionSelector =
  (extraData: IExtraData) => (state: IRootStore) => {
    const { documentType } = extraData;
    if (documentType === ERegistrationDocumentType.Brussels) {
      return rentRegistrationSelectors.isRetryingBrussels(
        state,
        extraData.registrationId,
      );
    }

    return rentRegistrationDocumentsSelectors.isUpdating(
      state,
      extraData.registrationId,
    );
  };

export const performingSubmitActionResultSelector =
  (extraData: IExtraData) => (state: IRootStore) =>
    rentRegistrationSelectors.getDetail(state, extraData.registrationId);

export const performingSubmitActionErrorSelector =
  (extraData: IExtraData) => (state: IRootStore) => {
    const { documentType } = extraData;
    if (documentType === ERegistrationDocumentType.Brussels) {
      return rentRegistrationSelectors.brusselsRetryError(
        state,
        extraData.registrationId,
      );
    }

    return rentRegistrationDocumentsSelectors.updateError(
      state,
      extraData.registrationId,
    );
  };

export const submitActionCreator =
  (extraData: IExtraData) => (formData: any) => {
    const {
      contract,
      property,
      documentType,
      registrationId,
      existingDocumentId,
    } = extraData;

    if (documentType === ERegistrationDocumentType.Brussels) {
      const { propertyAmenities } = extraData;

      const electricityEan = formData[EField.ElectricityEan];
      const parsedElectricityEan = electricityEan
        ? `${EAN_PREFIX}${electricityEan}`
        : undefined;

      const naturalGasEan = formData[EField.NaturalGasEan];
      const parsedNaturalGasEan = naturalGasEan
        ? `${EAN_PREFIX}${naturalGasEan}`
        : undefined;

      const updatedProperty: Partial<IProperty> | undefined = {
        id: property.id,

        // General
        constructionYear: formData[EField.ConstructionYear] ?? null,

        // EPC Fields
        epcLabel: formData[EEpcField.Label] ?? null,
        epcValue: formData[EEpcField.Value] ?? null,
        epcExpirationDate: formData[EEpcField.ExpirationDate] ?? null,
        epcCertificateId: formData[EEpcField.CertificateId] ?? null,

        // Electricity
        electricityType: formData[EField.ElectricityType] ?? null,
        electricityMeterNumberBoth:
          formData[EField.ElectricityMeterNumberBoth] ?? null,
        electricityEan: parsedElectricityEan,
        electricityMeterType: formData[EField.ElectricityMeterType] ?? null,

        // Natural Gas
        naturalGasAvailable: formData[EField.NaturalGasAvailable] ?? null,
        naturalGasEan: parsedNaturalGasEan,
        naturalGasMeterNumber: formData[EField.NaturalGasMeterNumber] ?? null,
        naturalGasMeterType: formData[EField.NaturalGasMeterType] ?? null,

        // Water
        waterMeterNumber: formData[EField.WaterMeterNumber] ?? null,

        // Exterior (garden/terrace stay on property)
        buildingGardenSurfaceArea:
          formData[EField.BuildingGardenSurfaceArea] ?? null,
        buildingTerraceSurfaceArea:
          formData[EField.BuildingTerraceSurfaceArea] ?? null,
      };

      // Build amenity items for upsert to propertyAmenity
      const amenityItems: (IUpsertPropertyAmenityItem | null)[] = [
        // Exterior
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.buildingAdjoined,
          formData[EField.BuildingAdjoined],
          "string",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.buildingSurfaceArea,
          formData[EField.BuildingSurfaceArea],
          "number",
        ),
        // Interior
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.buildingFloor,
          formData[EField.BuildingFloor],
          "number",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.buildingFloorCount,
          formData[EField.BuildingFloorCount],
          "number",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.roomCountBedroom,
          formData[EField.RoomCountBedroom],
          "number",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.roomCountBathroom,
          formData[EField.RoomCountBathroom],
          "number",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.roomCountGarage,
          formData[EField.RoomCountGarage],
          "number",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.buildingHasStorage,
          mapFormFieldToNullableBooleanValue(
            formData[EField.BuildingHasStorage],
          ),
          "boolean",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.isFurnished,
          mapFormFieldToNullableBooleanValue(formData[EField.IsFurnished]),
          "boolean",
        ),
        // Amenities
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.hasOven,
          mapFormFieldToNullableBooleanValue(formData[EField.HasOven]),
          "boolean",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.hasFridge,
          mapFormFieldToNullableBooleanValue(formData[EField.HasFridge]),
          "boolean",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.hasDishwasher,
          mapFormFieldToNullableBooleanValue(formData[EField.HasDishWasher]),
          "boolean",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.hasRangeHood,
          mapFormFieldToNullableBooleanValue(formData[EField.HasRangeHood]),
          "boolean",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.hasLift,
          mapFormFieldToNullableBooleanValue(formData[EField.HasLift]),
          "boolean",
        ),
        // Energy
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.hasDoubleGlazing,
          mapFormFieldToNullableBooleanValue(formData[EField.HasDoubleGlazing]),
          "boolean",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.hasCentralHeating,
          mapFormFieldToNullableBooleanValue(
            formData[EField.HasCentralHeating],
          ),
          "boolean",
        ),
        createAmenityItem(
          propertyAmenities,
          AMENITY_NAMES.heatingType,
          formData[EField.HeatingType],
          "string",
        ),
      ];

      // Filter out null items - only send changed amenities
      const newAmenityItems = amenityItems.filter(
        (item): item is IUpsertPropertyAmenityItem => item !== null,
      );

      // Amenities will be saved by the saga
      const updatedAmenities =
        newAmenityItems.length > 0 ? newAmenityItems : undefined;

      const updatedContract: Partial<IContract> | undefined = {
        id: contract.id,

        [EField.IsSharedLease]: mapFormFieldToNullableBooleanValue(
          formData[EField.IsSharedLease],
        ),
        [EField.ReferencePrice]: formData[EField.ReferencePrice] ?? null,
      };

      return rentRegistrationActions.brusselsRegistrationRetryStart.getAction({
        registrationId,
        updatedProperty,
        updatedContract,
        updatedAmenities,
      });
    }

    const signers = [
      ...(formData[EField.Owners] || []).map((accountId: number) => ({
        role: ERegistrationMemberType.Owner,
        accountId,
      })),
      ...(formData[EField.SignAsBroker] || []).map((accountId: number) => ({
        role: ERegistrationMemberType.Broker,
        accountId,
      })),
      ...(formData[EField.Tenants] || []).map((accountId: number) => ({
        role: ERegistrationMemberType.Tenant,
        accountId,
      })),
    ];

    const document: ICreateRegistrationDocument = {
      documentId: formData[EField.DocumentToUpload]?.[0],
      documentType,
      type: formData[EField.Type],
      signedAt:
        extraData.documentType === ERegistrationDocumentType.Addendum
          ? formData[EField.SignedAtAddendum]
          : formData[EField.SignedAt],
      signers,
    };

    if (existingDocumentId) {
      return rentRegistrationDocumentsActions.updateRegistrationDocumentStart.getAction(
        {
          registrationId: registrationId || -1,
          documentId: existingDocumentId,
          document: {
            ...document,
            status: ERegistrationDocumentStatus.Registering,
          },
        },
      );
    }

    return rentRegistrationDocumentsActions.addRegistrationDocumentStart.getAction(
      {
        registrationId: registrationId || -1,
        documents: [document],
      },
    );
  };
